<template>
  <div class="pa-2">
    <v-tabs show-arrows v-model="activeTab" slider-color="tertiary" v-allowed="'view_attendance'">
      <v-tab :key="'dashboard'" ripple @click="clickTab(0)">
        {{ $t("message.dashboard") }}
      </v-tab>
      <v-tab :key="'details'" ripple @click="clickTab(1)">
        {{ $t("message.details") }}
      </v-tab>
      <v-tab :key="'manual'" ripple @click="clickTab(2)">
        {{ $t("message.manual_checkin") }}
      </v-tab>
      <v-tab :key="'staff'" ripple @click="clickTab(3); loadStaffCheckins()">
        {{ $t("message.staff_checkins") }}
      </v-tab>
      <v-tabs-items v-model="activeTab" class="mt-6">
        <v-tab-item :key="'dashboard'" class="pa-2">
          <div>
            <div class="mt-4">
              <interval-select v-model="interval" />
            </div>
            <div v-if="loading" class="text-center">
              <v-skeleton-loader type="article, divider"></v-skeleton-loader>
            </div>
            <div v-else class="pa-0">
              <v-row align="start">
                <v-col cols="12" lg="6" class="pa-3">
                  <v-card class="pa-2 fill-height" outlined elevation="0">
                    <v-card-title class="text-subtitle-1">{{ $t("message.top_attendance") }}
                      <v-spacer></v-spacer>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-bind="attrs" v-on="on" icon small
                            @click="getAllAttendance()"><v-icon>mdi-account-group</v-icon></v-btn>
                        </template>
                        <span>{{ $t("message.view_all") }}</span>
                      </v-tooltip>
                    </v-card-title>
                    <podium v-if="topUsers" height="190px" :display-avatars="true" :podium-action="goToPodiumUser"
                      :display-scores="true" :data="topUsers">
                    </podium>
                    <div v-else style="height:190px" class="text-center pb-5">
                      <v-icon size="35">mdi-cancel</v-icon>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" lg="6" class="pa-2">
                  <v-card class="pa-2 fill-height" outlined elevation="0">
                    <v-card-title class="text-subtitle-1">{{ $t("message.interval_comparison") }}</v-card-title>
                    <v-card-actions v-if="comparisonNumbers" class="text-left pa-2" style="height:190px">
                      <v-spacer></v-spacer>
                      <div>
                        <div>{{ $t("message.this_interval") }}</div>
                        <div>
                          <span class="primary--text text-h1 font-weight-medium">{{ comparisonNumbers[1].value }}</span>
                          <span>{{ $t("message.from").toLowerCase() }}</span>
                          <span class="ml-2 font-weight-medium">{{ comparisonNumbers[0].value }}</span>
                        </div>
                      </div>
                      <div>
                        <v-btn elevation="0" text color="success" rounded
                          v-if="comparisonNumbers[1].value > comparisonNumbers[0].value">
                          <span>
                            <v-icon size="30" color="success">mdi-arrow-up-bold-circle-outline</v-icon>
                          </span>
                          <span v-if="!isSmallerDisplay()">{{ Math.abs(comparisonNumbers[1].percent) }}%</span>
                        </v-btn>
                        <v-btn elevation="0" color="error" text rounded v-else>
                          <span>
                            <v-icon color="error">mdi-arrow-down-bold-circle-outline</v-icon>
                          </span>
                          <span v-if="!isSmallerDisplay()" class="font-weight-bold">{{
                            Math.abs(comparisonNumbers[1].percent) }}%</span>
                        </v-btn>
                      </div>
                      <v-spacer></v-spacer>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
              <v-row align="start">
                <v-col cols="12" lg="6" class="pa-2">
                  <v-card class="pa-2 fill-height graph-area" outlined elevation="0">
                    <v-card-title class="text-subtitle-1">{{ $t("message.total_students_checkins") }}</v-card-title>
                    <ApexChart v-if="chartData1" type="pie" :options="chart1Options" height="350" :series="chartData1">
                    </ApexChart>
                  </v-card>
                </v-col>
                <v-col cols="12" lg="6" class="pa-2">
                  <v-card class="pa-2 fill-height graph-area" outlined elevation="0">
                    <v-card-title class="text-subtitle-1">{{ $t("message.attendance_day") }}</v-card-title>
                    <ApexChart v-if="chartData2 && chartData2[0]" type="line" height="350" :options="chart2Options"
                      :series="chartData2"></ApexChart>
                  </v-card>
                </v-col>
              </v-row>
              <v-row align="start">
                <v-col cols="12" lg="6" class="pa-2">
                  <v-card class="pa-2 fill-height graph-area" outlined elevation="0">
                    <v-card-title class="text-subtitle-1">{{ $t("message.checkins_by_source") }}</v-card-title>
                    <ApexChart v-if="chartData3" type="donut" :options="chart3Options" height="350"
                      :series="chartData3">
                    </ApexChart>
                  </v-card>
                </v-col>
                <v-col cols="12" lg="6" class="pa-2">
                  <v-card class="pa-2 fill-height" outlined elevation="0">
                    <v-card-title class="text-subtitle-1">{{ $t("message.attendance_day_week") }}</v-card-title>
                    <ApexChart v-if="chartData4" type="radar" :options="chart4Options" height="350"
                      :series="chartData4">
                    </ApexChart>
                  </v-card>
                </v-col>
              </v-row>
              <v-row align="start" v-if="heatMap && heatMap.data">
                <v-col cols="12">
                  <v-card class="pa-2 fill-height graph-area" outlined elevation="0">
                    <v-card-title class="text-subtitle-1">{{ $t("message.attendance_by_year_month") }}</v-card-title>
                    <ApexChart v-if="calendarChartData" type="heatmap" :options="calendarChartOptions" height="350"
                      :series="calendarChartData">
                    </ApexChart>
                  </v-card>
                </v-col>
              </v-row>
              <v-row align="start" v-if="heatMap && heatMap.data">
                <v-col cols="12">
                  <v-card class="pa-2 fill-height graph-area" outlined elevation="0">
                    <v-card-title class="text-subtitle-1">{{ $t("message.attendance_by_day_hour") }}</v-card-title>
                    <ApexChart type="heatmap" :options="heatMap.options" height="350" :series="heatMap.data">
                    </ApexChart>
                  </v-card>
                </v-col>
              </v-row>
              <div xs12 v-if="newMembers" class="text-center">
                <v-card>
                  <v-card-title>
                    <h3>{{ $t("message.new_members") }}</h3>
                  </v-card-title>
                  <v-card-text>
                    <member-avatar v-for="u in newMembers" class="clickable" :key="'member_avatar_checkin_' + u._id"
                      :id="u._id" :image="u.userImageSmall ? u.userImageSmall : u.userImage" :size="64"
                      style="margin-top:5px;margin-left:5px">
                    </member-avatar>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </div>
        </v-tab-item>
        <v-tab-item :key="'details'">
          <!-- Section toggle -->
          <v-card-text class="pb-1">
            <div class="d-flex justify-center mb-2">
              <v-btn-toggle v-model="detailsSection" mandatory rounded color="primary" dense>
                <v-btn value="details" small class="px-6 font-roboto text-capitalize">
                  {{ $t('message.details') }}
                </v-btn>
                <v-btn value="aggregates" small class="px-6 font-roboto text-capitalize">
                  {{ $t('message.aggregated') }}
                </v-btn>
              </v-btn-toggle>
            </div>
          </v-card-text>

          <!-- ── Details sub-section ───────────────────────────────────── -->
          <div v-show="detailsSection === 'details'">
            <v-card fluid class="elevation-0">
              <v-card-text>
                <div>
                  <v-row no-gutters>
                    <v-col cols="12" xs="12" lg="8">
                      <v-autocomplete v-model="selectedMember" :items="members" :label="$t('message.filter_member')"
                        item-text="text" item-value="value" clearable outlined dense></v-autocomplete>
                      <v-autocomplete v-if="professors" v-model="selectedProfessor" :items="professors"
                        :label="$t('message.filter_professor')" item-text="name" item-value="_id" clearable outlined
                        dense></v-autocomplete>
                      <v-select :items="availableClassNames" v-model="selectedClassName"
                        :label="$t('message.filter_class_name')" clearable outlined dense></v-select>
                      <v-select v-if="availableLocations && availableLocations.length > 0" :items="availableLocations"
                        v-model="selectedLocation" :label="$t('message.location')" clearable outlined dense></v-select>
                      <v-select v-if="availableDisciplines && availableDisciplines.length > 0"
                        :items="availableDisciplines" v-model="selectedDiscipline" :label="$t('message.discipline')"
                        clearable outlined dense></v-select>
                      <v-select :items="availableClasses" item-text="text" item-value="value" v-model="selectedClass"
                        :label="$t('message.filter_class_date')" clearable outlined dense></v-select>
                      <month-select v-model="selectedMonth" />
                      <v-select hide-details clearable :items="availableSources" item-text="text" item-value="value"
                        v-model="selectedSource" :label="$t('message.filter_source')" outlined dense></v-select>
                      <v-switch hide-details :disabled="selectedMember != null" v-model="membersOnly"
                        :label="$t('message.members_only')">
                      </v-switch>
                      <v-switch hide-details :disabled="selectedMember != null" v-model="visitorsOnly"
                        :label="$t('message.visitors_only')">
                      </v-switch>
                      <v-switch v-if="selectedMonth" hide-details v-model="sortByCheckins"
                        @change="sortByCheckinCounts()" :label="$t('message.sort_assistance_count')"></v-switch>
                    </v-col>
                    <v-col cols="12" xs="12" lg="4">
                      <v-card outlined flat class="pa-2 text-center ma-1">
                        <div>
                          <v-date-picker v-if="today" :locale="$i18n.locale" :max="today" v-model="datePicker">
                          </v-date-picker>
                        </div>
                        <v-card-actions>
                          <v-btn block outlined @click="clearParameters()">{{ $t('message.clear_parameters') }}</v-btn>
                        </v-card-actions>
                        <v-card-actions>
                          <v-btn block outlined color="primary" :loading="loadingAttendanceCsv"
                            @click="getAttendanceCSV()">{{
                              $t('message.generate_report') }}</v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
            </v-card>
            <v-container v-if="rollingNumbers && !selectedMonth" class="lighten-5">
              <v-row>
                <v-col v-for="(n, index) in rollingNumbers" :key="'rolling_number_month_' + index" cols="12" sm="4">
                  <v-card class="pa-2" outlined tile>
                    <v-card-title>{{ n.title }}</v-card-title>
                    <v-card-text class="text-center"><span class="rolling-value mr-2">{{ n.value }}</span>
                      <span class="mr-2">
                        <v-icon size="30" color="red" v-if="n.percent < 0">mdi-arrow-down-bold-circle-outline
                        </v-icon>
                        <v-icon size="30" color="success"
                          v-else-if="n.percent > 0">mdi-arrow-up-bold-circle-outline</v-icon>
                      </span>
                      <span v-if="n.percent != 0" class="rolling-percent">
                        {{ Math.abs(n.percent) }}%
                      </span>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
            <div v-if="checkins && !sortByCheckins">
              <div v-if="checkins && checkins.length > 0">
                <v-list two-line subheader>
                  <v-subheader>
                    <span>{{ $t('message.total_results') }}: {{ totalResults }}<span v-if="detailsOnTimeRate !== null"> ({{ detailsOnTimeRate }}% {{ $t('message.on_time_rate') }})</span></span>
                  </v-subheader>
                  <div class="mr-2 ml-2">
                    <v-text-field clearable @input="filterResults()" hide-details outlined dense
                      :label="$t('message.filter_results')" v-model="filterResultsByName"></v-text-field>
                  </div>
                  <template v-for="(checkin, index) in filteredCheckins">
                    <v-list-item :key="'checkin_' + index + '_' + checkin._id">
                      <v-list-item-avatar @click="viewDetails(checkin.user)" class="clickable">
                        <ImageDisplay v-if="checkin.user && checkin.user.userImageSmall"
                          :src="checkin.user.userImageSmall" />
                        <img v-else :src="assets() + '/default-profile-avatar.png'" />
                      </v-list-item-avatar>
                      <v-list-item-action class="mr-4">
                        <v-icon title="Tablet" v-if="checkin.source == 'tablet'">mdi-tablet</v-icon>
                        <v-icon v-else-if="checkin.source == 'manual'">mdi-desktop-classic</v-icon>
                        <v-icon v-else>mdi-cellphone-sound
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content class="text-left">
                        <v-list-item-title>
                          <v-tooltip top v-if="checkin.isAssistantProfessor">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on" size="20"
                                color="accent">mdi-account-supervisor-outline</v-icon>
                            </template>
                            <span>{{ $t("message.assistant_professor") }}</span>
                          </v-tooltip>
                          <v-tooltip top v-else-if="checkin.isProfessor">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on" size="20"
                                color="accent">mdi-account-star-outline</v-icon>
                            </template>
                            <span>{{ $t("message.professor") }}</span>
                          </v-tooltip>
                          <span v-if="checkin.user">{{ checkin.user.displayName }}</span>
                          <span v-else-if="checkin.visitorData">{{ checkin.visitorData.name }}</span>
                          <span v-if="!checkin.isMember" class="primary--text ml-1 text-caption">({{
                            $t("message.visitor")
                            }})</span>
                          <BeltDisplay class="ml-1 mb-1" :show-text="false" size="12"
                            v-if="checkin.belt && checkin.user && !checkin.user.notBjjUser" :belt="checkin.belt">
                          </BeltDisplay>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <span v-if="checkin.name && checkin.name != ''">
                            {{ checkin.name }}
                          </span>
                          <v-tooltip v-if="checkin.academy && checkin.academy._id != $store.state.selectedAcademy" top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on" size="20" color="accent">mdi-bank</v-icon>
                            </template>
                            <span>{{ checkin.academy.name }}</span>
                          </v-tooltip>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          <v-tooltip v-if="checkin.isLate" top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on" size="20" color="error">mdi-clock-alert-outline</v-icon>
                            </template>
                            <span>{{ $t("message.minutes_late", [checkin.isLate]) }}</span>
                          </v-tooltip>
                          {{ $d(new Date(checkin.date), "long", $i18n.locale) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action v-if="checkin.checkout">
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon>
                              <v-icon>mdi-logout</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("message.checkout") }}: {{ $d(new Date(checkin.checkout), "long", $i18n.locale)
                            }}</span>
                        </v-tooltip>
                      </v-list-item-action>
                      <v-list-item-action>
                        <v-tooltip
                          v-if="checkin.membershipStatus && checkin.academy && checkin.academy._id == $store.state.selectedAcademy"
                          top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon
                              @click="goToTransaction(checkin.membershipStatusTransaction)">
                              <v-icon
                                :color="getTransactionStatusColor(checkin.membershipStatus)">mdi-ticket-confirmation-outline
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("message." + checkin.membershipStatus) }}</span>
                        </v-tooltip>
                      </v-list-item-action>
                      <v-list-item-action>
                        <v-btn v-if="$store.state.managedAcademy == checkin.managedAcademy" icon
                          @click="showDeleteCheckinDialog(checkin._id)">
                          <v-icon>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="index + 1 < filteredCheckins.length"
                      :key="'regular_attendance_' + index"></v-divider>
                  </template>
                </v-list>
              </div>
              <div v-else class="mt-1 text-center text-h6">
                {{ $t("message.no_data") }}
              </div>
            </div>
            <div v-else-if="checkinsAggregated">
              <div v-if="checkinsAggregated.length > 0">
                <v-list two-line subheader>
                  <div class="mr-2 ml-2">
                    <v-text-field clearable @input="filterResults()" hide-details outlined dense
                      :label="$t('message.filter_results')" v-model="filterResultsByName"></v-text-field>
                  </div>
                  <template v-for="(checkin, index) in filteredCheckinsAggregated">
                    <v-list-item :key="'aggregated_' + checkin._id">
                      <v-list-item-avatar @click="viewDetails(checkin)" class="clickable">
                        <ImageDisplay :src="checkin.userImageSmall" />
                      </v-list-item-avatar>
                      <v-list-item-content class="text-left">
                        <v-list-item-title>
                          {{ checkin.displayName }}
                          <BeltDisplay :show-text="false" size="12" v-if="checkin.belt" :belt="checkin.belt">
                          </BeltDisplay>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <span style="font-size:large;font-weight: bold;">{{ checkin.checkins }}</span> {{
                            $t("message.checkins") }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider v-if="index + 1 < filteredCheckinsAggregated.length"
                      :key="'checkin_aggregated_' + index"></v-divider>
                  </template>
                </v-list>
              </div>
              <div v-else class="mt-1 text-center text-h6">
                {{ $t("message.no_data") }}
              </div>
            </div>
            <div v-if="loading">
              <circles-loader></circles-loader>
            </div>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn outlined elevation="0" small v-if="viewMore" color="primary" @click="getAttendance(true)">{{
                $t("message.view_more") }}
              </v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </div>
          <!-- end details sub-section -->

          <!-- ── Aggregates sub-section ──────────────────────────────── -->
          <div v-show="detailsSection === 'aggregates'">
            <v-card-text>
              <v-row no-gutters>
                <v-col cols="12" lg="6">
                  <div class="text-subtitle-2 mb-1">{{ $t('message.start_date') }}</div>
                  <DatePicker :past-event="true" v-model="aggStartDate" />
                  <div class="text-subtitle-2 mt-3 mb-1">{{ $t('message.end_date') }}</div>
                  <DatePicker :past-event="true" v-model="aggEndDate" />
                  <v-select v-model="reportGroupBy" :items="aggGroupByOptions" :label="$t('message.group_by')" outlined
                    dense class="mt-3" hide-details />
                  <v-switch v-model="aggGenerateExport" hide-details :label="$t('message.generate_export')"
                    class="mt-3" />
                  <v-switch v-if="aggGenerateExport" v-model="reportAggregateByUser" hide-details
                    :label="$t('message.aggregate_by_user')" class="mt-1" />
                  <div class="mt-4 d-flex" style="gap:8px;">
                    <v-btn v-if="!aggGenerateExport" outlined small elevation="0" color="primary"
                      :loading="loadingAggResults" @click="getAggregateResults()">
                      {{ $t('message.get_results') }}
                    </v-btn>
                    <v-btn v-else outlined small elevation="0" color="primary" @click="getMemberAttendanceReport()">
                      {{ $t('message.generate_report') }}
                    </v-btn>
                  </div>
                  <div class="text-caption mt-2">*{{ $t('message.aggregated_data_utc') }}</div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-text v-if="!aggGenerateExport && aggResults">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>{{ reportGroupBy === 'month' ? $t('message.month') : $t('message.class') }}</th>
                    <th>{{ $t('message.checkins') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in aggResults" :key="'agg_row_' + i">
                    <td>{{ row.month || row.class }}</td>
                    <td>{{ row.attendance }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </div>
          <!-- end aggregates sub-section -->

        </v-tab-item>
        <v-tab-item :key="'manual'">
          <v-card-text>
            <v-autocomplete :disabled="visitorCheckin != null" v-model="memberCheckin" :items="members"
              :label="$t('message.pick_member')" item-text="text" item-value="value" clearable outlined
              dense></v-autocomplete>
            <v-autocomplete :disabled="memberCheckin != null" v-if="visitors" v-model="visitorCheckin" :items="visitors"
              :label="$t('message.pick_visitor')" item-text="text" item-value="value" clearable outlined
              dense></v-autocomplete>
            <v-select :items="checkinClasses" item-text="text" :return-object="true" v-model="classCheckin"
              :label="$t('message.pick_class')" clearable outlined dense></v-select>
          </v-card-text>
          <v-card-actions>
            <v-btn outlined elevation="0" :disabled="!classCheckin || (!memberCheckin && !visitorCheckin)" small
              color="primary" :loading="checkingIn" @click="manualMemberCheckin()">
              checkin
            </v-btn>
          </v-card-actions>
        </v-tab-item>
        <v-tab-item :key="'staff_checkins'">
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn elevation="0" @click="switchStaffCheckinsMode(false)"
              :color="byClassCheckins ? '' : 'primary'" :outlined="byClassCheckins ? false : true">
              {{ $t('message.by_time') }}
            </v-btn>
            <v-btn elevation="0" @click="switchStaffCheckinsMode(true)" class="ml-1"
              :color="byClassCheckins ? 'primary' : ''" :outlined="byClassCheckins ? true : false">
              {{ $t('message.by_class') }}
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
          <v-card elevation="0" outlined v-if="professors && byClassCheckins">
            <v-card-text v-if="byClassCheckins">
              <v-autocomplete v-if="professors" v-model="staffCheckinsProfessor" :items="professors"
                :label="$t('message.filter_professor')" item-text="name" item-value="_id" clearable outlined
                dense></v-autocomplete>
              <v-row>
                <v-col>
                  <div class="text-subtitle-2">{{ $t('message.from') }}</div>
                  <div>
                    <DatePicker :past-event="true" v-model="staffCheckinsFrom"></DatePicker>
                  </div>
                </v-col>
                <v-col>
                  <div class="text-subtitle-2">{{ $t('message.to') }}</div>
                  <div>
                    <DatePicker :past-event="true" v-model="staffCheckinsTo"></DatePicker>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-switch dense hide-details v-model="staffCheckinsNotPaid"
                    :label="$t('message.unpaid_only')"></v-switch>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn :loading="loadingStaffCheckins" outlined
                    :disabled="loadingStaffCheckins || !staffCheckinsProfessor" elevation="0" color="primary"
                    @click="loadStaffCheckins()">
                    {{ $t('message.filter') }}
                  </v-btn>
                  <v-btn :loading="loadingStaffCheckins" v-if="!isSmallerDisplay()" class="ml-1" outlined
                    :disabled="loadingStaffCheckins || !staffCheckinsProfessor" elevation="0" color="primary"
                    @click="getCheckinsCSV()">
                    <v-icon class="mr-1">mdi-cloud-download-outline</v-icon>
                    {{ $t('message.download') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card flat v-else outlined>
            <v-card-text>
              <v-autocomplete v-if="professors" v-model="staffCheckinsProfessor" :items="professors"
                :label="$t('message.staff')" item-text="name" item-value="_id" clearable outlined
                dense></v-autocomplete>
              <v-row>
                <v-col>
                  <div class="text-subtitle-2">{{ $t('message.from') }}</div>
                  <div>
                    <DatePicker :past-event="true" v-model="staffCheckinsFrom"></DatePicker>
                  </div>
                </v-col>
                <v-col>
                  <div class="text-subtitle-2">{{ $t('message.to') }}</div>
                  <div>
                    <DatePicker :past-event="true" v-model="staffCheckinsTo"></DatePicker>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn :loading="loadingStaffCheckins" outlined elevation="0" color="primary"
                    @click="loadStaffCheckins()">
                    {{ $t('message.filter') }}
                  </v-btn>
                  <v-btn :loading="loadingStaffCheckins" v-if="!isSmallerDisplay()" class="ml-1" outlined elevation="0"
                    color="primary" @click="getCheckinsCSV()">
                    <v-icon class="mr-1">mdi-cloud-download-outline</v-icon>
                    {{ $t('message.download') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card flat v-if="selectedStaffCheckins.length > 0" outlined class="mt-2">
            <v-col>
              <div>
                <v-text-field @keyup="updateExpense()" dense outlined type="number"
                  :label="byClassCheckins ? $t('message.rate_per_class') : $t('message.hourly_salary')"
                  v-model="payRate"></v-text-field>
              </div>
              <div v-if="!byClassCheckins" class="mb-4">
                <v-switch dense hide-details class="mt-0" v-model="prorateStaffMinutes" @change="changeProrateStaffMinutes()"
                  :label="$t('message.prorate_minutes')"></v-switch>
              </div>
              <div>
                <v-text-field dense outlined type="number" :label="'Total (' + currency + ')'"
                  v-model="totalExpense"></v-text-field>
              </div>
              <div>
                <v-btn :loading="addingExpense" outlined small v-if="selectedStaffCheckins.length > 0" elevation="0"
                  color="primary" @click="addExpense()">
                  <v-icon v-if="loadingStaffCheckins" class="mr-1 rotate">mdi-refresh</v-icon>
                  {{ $t('message.add_expense') }}
                </v-btn>
              </div>
            </v-col>
          </v-card>
          <v-skeleton-loader v-if="loadingStaffCheckins" type="list-item-avatar, list-item-avatar, list-item-avatar">
          </v-skeleton-loader>
          <v-list two-line v-else>
            <v-subheader>
              <span>{{ $t('message.total_results') }}: {{ staffCheckins ? staffCheckins.length : 0 }}</span>
              <span class="ml-2" v-if="byClassCheckins && staffCheckins.length">({{ roundNumber(onTimeRate) }}% {{
                $t('message.on_time_rate')
                }})</span>
            </v-subheader>
            <v-switch v-if="staffCheckins && staffCheckins.length" hide-details v-model="selectAllStaffCheckins"
              :label="$t('message.select_all')">
            </v-switch>
            <template v-for="(checkin, index) in staffCheckins">
              <v-list-item v-if="checkin.user" :key="'staff_checkin_' + checkin._id">
                <v-list-item-avatar @click="goToMember(checkin.user._id, checkin.user.displayName)" class="clickable">
                  <ImageDisplay v-if="checkin.user && checkin.user.userImageSmall" :src="checkin.user.userImageSmall" />
                  <img v-else :src="assets() + '/default-profile-avatar.png'" />
                </v-list-item-avatar>
                <v-list-item-content class="text-left">
                  <v-list-item-title>
                    <v-tooltip top v-if="checkin.isAssistantProfessor">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" size="20"
                          color="accent">mdi-account-supervisor-outline</v-icon>
                      </template>
                      <span>{{ $t("message.assistant_professor") }}</span>
                    </v-tooltip>
                    {{ checkin.user.displayName }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $d(new Date(checkin.date), "month_day", $i18n.locale) }}
                    <span class="ml-1" v-if="staffCheckinsProfessor && checkin.name">- {{ checkin.name }}</span>
                    <span v-if="checkin.checkout">- {{ staffCheckinHours(checkin) }} {{
                      $t('message.hours') }}</span>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle>
                    <v-icon size="20" color="success">mdi-location-enter</v-icon>
                    {{ $d(new Date(checkin.date), "time", $i18n.locale) }}
                    <v-icon v-if="checkin.isLate" size="15" color="error" class="ml-1">mdi-clock-alert-outline</v-icon>
                    <span v-if="checkin.isLate" class="ml-1 error--text">{{ $t("message.minutes_late", [checkin.isLate])
                      }}</span>
                    <span class="mr-1" v-if="!byClassCheckins">-</span>
                    <v-icon v-if="!byClassCheckins && checkin.checkout" color="error"
                      size="20">mdi-location-exit</v-icon>
                    <v-icon v-else-if="!byClassCheckins" size="25" class="rotate">mdi-cog-clockwise</v-icon>
                    <span v-if="checkin.checkout">{{ $d(new Date(checkin.checkout), "time", $i18n.locale) }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="checkin.paid">
                  <div>
                    <v-icon color="success" class="mr-1">mdi-cash-multiple</v-icon>
                    {{ currency }}{{ checkin.paid.amount }}
                  </div>
                  <div v-if="checkin.paid.date" class="text-caption">
                    {{ $d(new Date(checkin.paid.date), "month_day_year", $i18n.locale) }}
                  </div>
                </v-list-item-action>
                <v-list-item-action v-else-if="!byClassCheckins && !checkin.checkout">
                  <v-btn icon small @click="showCheckoutDialog(checkin)">
                    <v-icon color="error">mdi-location-exit</v-icon>
                  </v-btn>
                </v-list-item-action>
                <v-list-item-action v-else-if="staffCheckinsProfessor">
                  <div>
                    <v-checkbox @change="updateExpense()" dense v-model="selectedStaffCheckins" :value="checkin._id"
                      hide-details></v-checkbox>
                  </div>
                  <div>
                    <span v-if="!isSmallerDisplay()">{{ $t('message.add_expense') }}</span>
                  </div>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index + 1 < staffCheckins.length" :key="'staff_checkins_' + index"></v-divider>
            </template>
          </v-list>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
    <v-dialog v-model="deleteCheckinDialog" max-width="90%">
      <v-card class="pa-2">
        <v-card-text>{{ $t('message.are_you_sure') }}</v-card-text>
        <v-card-actions>
          <v-btn small elevation="0" color="error" @click.stop="removeManualCheckin()">{{ $t('message.delete')
            }}</v-btn>
          <v-btn small elevation="0" @click.stop="deleteCheckinDialog = false; checkinToDelete = null;">{{
            $t('message.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="checkoutDialog" max-width="90%">
      <v-card class="pa-2" v-if="selectedStaffCheckin">
        <v-card-text>{{ $t('message.checkout_time') }}</v-card-text>
        <v-card-text>
          <DatePicker :past-event="true" v-model="selectedStaffCheckin.checkoutTime" :show-time="true"></DatePicker>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined small elevation="0" color="primary" @click.stop="addCheckoutTime()">{{ $t('message.update')
            }}</v-btn>
          <v-btn small elevation="0" @click.stop="checkoutDialog = false; selectedStaffCheckin = true">{{
            $t('message.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="allAttendanceDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card outlined>
        <v-toolbar dense flat class="opposite--text" color="primary">
          {{ allAttendance ? allAttendance.length : 0 }} {{ $t('message.members') }}
          <v-spacer></v-spacer>
          <v-btn icon small @click="allAttendanceDialog = false;" color="opposite">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <v-skeleton-loader v-if="loadingAllAttendance"
          type="list-item-avatar, divider, list-item-avatar, divider"></v-skeleton-loader>
        <div v-else>
          <div class="ml-2">
            <v-switch hide-details @change="onIncludeProfessorsChange()" :label="$t('message.include_professors')"
              v-model="includeProfessors"></v-switch>
            <v-switch v-if="includeProfessors" hide-details @change="getAllAttendance()"
              :label="$t('message.only_professors')" v-model="onlyProfessors"></v-switch>
            <v-switch hide-details @change="onMinorsOnlyChange()" :label="$t('message.minors_only')"
              v-model="minorsOnly"></v-switch>
            <v-switch @change="onAdultsOnlyChange()" :label="$t('message.adults_only')" v-model="adultsOnly"></v-switch>
            <TagsCombo class="mb-3" :label="$t('message.label')" :value="selectedLabel ? [selectedLabel] : []"
              @input="onLabelChange"></TagsCombo>
          </div>
          <v-list>
            <template v-for="(checkin, index) in allAttendance">
              <v-list-item @click="viewDetails(checkin)" :key="'all_attendance_' + checkin._id">
                <v-list-item-avatar class="clickable">
                  <ImageDisplay v-if="checkin.userImageSmall" :src="checkin.userImageSmall" />
                  <img v-else :src="assets() + '/default-profile-avatar.png'" />
                </v-list-item-avatar>
                <v-list-item-content class="text-left">
                  <v-list-item-title>
                    {{ checkin.displayName }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <BeltDisplay :show-text="true" size="12" v-if="checkin.belt" :belt="checkin.belt">
                    </BeltDisplay>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  {{ checkin.checkins }}
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index + 1 < allAttendance.length" :key="'all_attendance_' + index"></v-divider>
            </template>
          </v-list>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import BeltDisplay from "@/components/displays/BeltDisplay.vue";
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import MemberAvatar from "@/components/displays/MemberAvatar.vue";
import Podium from "@/components/displays/PodiumDisplay.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import IntervalSelect from "@/components/inputs/IntervalSelect.vue";
import MonthSelect from "@/components/inputs/MonthSelect.vue";
import TagsCombo from "@/components/inputs/TagsCombo.vue";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";
import ApexChart from 'vue-apexcharts';

export default {
  components: {
    CirclesLoader,
    MonthSelect,
    DatePicker,
    ApexChart,
    IntervalSelect,
    MemberAvatar,
    Podium,
    BeltDisplay,
    TagsCombo
  },
  data() {
    return {
      urlTab: this.$route.params.tabId,
      urlUser: this.$route.query.userId,
      activeTab: 0,
      loading: false,
      showCalendar: false,
      deleteCheckinDialog: false,
      attendanceReportDialog: false,
      detailsSection: 'details',
      aggGenerateExport: false,
      aggResults: null,
      loadingAggResults: false,
      aggStartDate: null,
      aggEndDate: null,
      filterResultsByName: null,
      rollingNumbers: null,
      checkins: null,
      checkinsAggregated: null,
      membersOnly: false,
      sortByCheckins: false,
      totalResults: 0,
      professors: null,
      visitorsOnly: false,
      members: [],
      visitors: [],
      visitorCheckin: null,
      availableClasses: [],
      availableClassNames: [],
      selectedClassName: null,
      availableLocations: [],
      selectedLocation: null,
      availableDisciplines: [],
      selectedDiscipline: null,
      checkinClasses: [],
      selectedMember: null,
      memberCheckin: null,
      classCheckin: null,
      checkingIn: false,
      dateCheckin: null,
      selectedClass: null,
      selectedDate: null,
      selectAllStaffCheckins: false,
      selectedMonth: null,
      selectedProfessor: null,
      today: null,
      datePicker: null,
      checkoutTime: null,
      viewMore: false,
      timeZone: null,
      schedule: null,
      exclude: [],
      index: 0,
      intervalText: null,
      topUsers: null,
      interval: null,
      from: null,
      to: null,
      comparisonNumbers: null,
      chartData1: null,
      chartData2: null,
      chartData3: null,
      chartData4: null,
      heatMap: { loading: false, options: {}, data: null },
      calendarChartData: null,
      calendarChartOptions: null,
      loadingStaffCheckins: false,
      loadingAttendanceCsv: false,
      staffCheckins: [],
      payRate: null,
      currency: "$",
      totalExpense: 0,
      prorateStaffMinutes: false,
      onTimeRate: 0,
      selectedStaffCheckins: [],
      selectedStaffCheckin: null,
      staffCheckinsNotPaid: false,
      newMembers: null,
      checkoutDialog: null,
      chart1Options: {},
      chart2Options: {},
      classesMap: {},
      loadingAllAttendance: false,
      allAttendanceDialog: null,
      allAttendance: [],
      availableSources: [],
      selectedSource: null,
      includeProfessors: false,
      onlyProfessors: false,
      minorsOnly: false,
      adultsOnly: false,
      selectedLabel: null,
      addingExpense: false,
      reportGroupBy: "class",
      reportAggregateByUser: true,
      reportGroupByOptions: [
        { text: "None", value: "none" },
        { text: "Class", value: "class" },
        { text: "Month", value: "month" },
      ],
      filteredCheckins: [],
      filteredCheckinsAggregated: [],
      chart3Options: {
        title: this.$t("message.checkins_by_source"),
        theme: {
          mode: this.isDarkMode() ? 'dark' : 'light',
        },
        pieHole: 0.4,
        pieSliceText: "value",
        hAxis: { textPosition: "none" },
        legend: { position: "bottom", alignment: "center", textStyle: { color: this.themeColor('title') } },
        vAxis: {
          viewWindow: {
            min: 0,
          },
        },
        colors: [this.themeColor('graph1'), this.themeColor('graph2'), this.themeColor('graph3'), this.themeColor('secondary')],
      },
      chart4Options: {},
      chartCalendarOptions: {
        height: 350,
      },
      weekdays: [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ],
      byClassCheckins: false,
      staffCheckinsFrom: null,
      staffCheckinsTo: null,
      staffCheckinsProfessor: null,
    };
  },
  mounted() {
    if (this.urlTab) {
      try {
        const n = new Number(this.urlTab);
        this.activeTab = n.valueOf();
        if (this.activeTab == 3)
          this.loadStaffCheckins();
      } catch (err) {
        console.log('Invalid tab: ', this.urlTab)
      }
    }
  },
  created() {
    var tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    this.today = new Date(Date.now() - tzoffset).toISOString().slice(0, -1);
    this.dateCheckin = new Date().toISOString().substr(0, 10);
    this.loading = false;
    const yearStart = new Date();
    yearStart.setMonth(0, 1);
    yearStart.setHours(0, 0, 0, 0);
    this.aggStartDate = yearStart;
    this.aggEndDate = new Date();
    if (!this.isSmallerDisplay()) this.getCheckinsCalendar();
    this.staffCheckinsFrom = new Date();
    this.staffCheckinsFrom.setDate(1);
    this.prorateStaffMinutes = this.savedProrateStaffMinutes();
    Api2()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        const academy = response.data;
        if (academy.city) {
          this.timeZone = academy.city.timeZone;
        }
        let schedule = response.data.schedule;
        this.schedule = schedule;
        let classNames = new Set();
        let locations = new Set();
        let disciplines = new Set();
        for (const d in schedule) {
          for (const c of schedule[d]) {
            let text =
              this.$t("message." + d) + ": " + c.name + " (" + this.numberToTime(c.start) + ")";
            classNames.add(c.name);
            if (c.location) locations.add(c.location);
            if (c.discipline) disciplines.add(c.discipline);
            this.availableClasses.push({ value: c._id, text: text, professor: c.professorUser });
            this.checkinClasses.push({ value: c._id, text: text, professor: c.professorUser });
          }
        }
        this.availableClassNames = Array.from(classNames);
        this.availableLocations = Array.from(locations).sort();
        this.availableDisciplines = Array.from(disciplines).sort();
        this.interval = 2;
      })
      .catch((e) => {
        if (e)
          this.showAlert("error", "Ops!", this.$t("message.error_generic"));
      });
    Api2()
      .get("admins/academy/" + this.$store.state.selectedAcademy + "/members")
      .then((response) => {
        const data = response.data;
        if (data) {
          for (const m of data.approved) {
            this.members.push({ value: m._id, text: m.fName + " " + m.lName });
          }
        }
      })
      .catch((e) => {
        if (e)
          this.showAlert("error", "Ops!", this.$t("message.error_generic"));
      });
    Api2()
      .get("admins/academy/" + this.$store.state.managedAcademy + "/visitors?exclude_rejected=true")
      .then((response) => {
        const data = response.data;
        if (data && data.list && data.list.length) {
          for (const v of data.list) {
            if (v && v.user)
              this.visitors.push({ value: v.user._id, text: v.user.displayName });
          }
        }
      });
    if (this.$store.state.managedAcademy) {
      this.updateNotificationsCount();
      this.classesMap = {};
      Api2()
        .get("calendar/admin/academy/" + this.$store.state.managedAcademy + "/checkins/events")
        .then((response) => {
          const data = response.data;
          if (data) {
            for (const academyClass of data.events) {
              const text = this.$d(new Date(academyClass.date), "month_day", this.$i18n.locale) + ": " + academyClass.title;
              this.checkinClasses.push({ value: academyClass.id, text: text, professor: academyClass.professorUser });
              this.classesMap[academyClass.id] = academyClass;
            }
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Ops!", this.$t("message.error_generic"));
        });
      Api2()
        .get("calendar/admin/academy/" + this.$store.state.managedAcademy + "/professors?get_staff=true")
        .then((response) => {
          const data = response.data;
          if (data) {
            if (data.professors.length > 0)
              this.professors = data.professors;
          }
        });
      if (this.urlTab && this.urlUser) {
        try {
          const n = new Number(this.urlTab).valueOf();
          if (this.urlUser && n == 1) {
            this.selectedMember = this.urlUser;
          }
        } catch (err) {
          console.log('Invalid tab: ', this.urlTab)
        }
      }
    }
    this.availableSources.push({ text: this.$t("message.tablet"), value: 'tablet' });
    this.availableSources.push({ text: this.$t("message.app"), value: 'app_social' });
    this.availableSources.push({ text: this.$t("message.manual"), value: 'manual' });
  },
  computed: {
    detailsOnTimeRate() {
      const list = this.filteredCheckins;
      if (!list || !list.length) return null;
      const onTime = list.filter(c => !c.isLate).length;
      return Math.round((onTime / list.length) * 100);
    },
    aggGroupByOptions() {
      const cap = (s = '') => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
      const opts = [
        { text: cap(String(this.$t('message.class'))), value: 'class' },
        { text: cap(String(this.$t('message.month'))), value: 'month' },
      ];
      if (this.aggGenerateExport) {
        opts.unshift({ text: cap(String(this.$t('message.none'))), value: 'none' });
      }
      return opts;
    },
  },
  watch: {
    aggGenerateExport(val) {
      if (!val && this.reportGroupBy === 'none') {
        this.reportGroupBy = 'class';
      }
      this.aggResults = null;
    },
    '$route'(to) {
      this.activeTab = to.params.tabId;
    },
    interval: {
      handler: function (newValue) {
        this.getAggregates(newValue);
      },
    },
    membersOnly: {
      handler: function (newValue) {
        if (newValue) this.visitorsOnly = false;
        this.getAttendance();
      },
    },
    selectedSource: {
      handler: function () {
        this.getAttendance();
      },
    },
    selectedLocation: {
      handler: function () {
        this.getAttendance();
      },
    },
    selectedDiscipline: {
      handler: function () {
        this.getAttendance();
      },
    },
    visitorsOnly: {
      handler: function (newValue) {
        if (newValue) this.membersOnly = false;
        this.getAttendance();
      },
    },
    selectedMember: {
      handler: function (newValue) {
        if (newValue) {
          this.getAttendance();
          this.getRollingNumbers();
        } else {
          this.clearAttendances();
        }
      },
    },
    selectedProfessor: {
      handler: function (newValue) {
        if (newValue) {
          this.getAttendance();
          this.getRollingNumbers();
        } else {
          this.clearAttendances();
        }
      },
    },
    selectedClass: {
      handler: function (newValue) {
        if (newValue) {
          this.getAttendance();
          this.getRollingNumbers();
        } else {
          this.clearAttendances();
        }
      },
    },
    selectedClassName: {
      handler: function (newValue) {
        if (newValue) {
          this.getAttendance();
          this.getRollingNumbers();
        } else {
          this.clearAttendances();
        }
      },
    },
    selectedMonth: {
      handler: function (newValue) {
        if (newValue) {
          this.selectedDate = null;
          this.getAttendance();
          this.getRollingNumbers();
        } else {
          this.clearAttendances();
        }
      },
    },
    datePicker: {
      handler: function (newValue) {
        this.selectedMonth = null;
        if (newValue) {
          let d = new Date(newValue);
          const split = newValue.split('-');
          if (split.length == 3) {
            d = new Date(split[0], new Number(split[1]).valueOf() - 1, split[2]);
          }
          this.selectedDate = d;
          this.getAttendance();
        }
      },
    },
    selectAllStaffCheckins: {
      handler: function (newValue) {
        this.selectedStaffCheckins = [];
        if (newValue) {
          for (const i of this.staffCheckins) {
            this.selectedStaffCheckins.push(i._id);
          }
          this.updateExpense();
        }
      },
    }
  },
  methods: {
    clearParameters() {
      this.selectedDate = null;
      this.selectedMonth = null;
      this.selectedClassName = null;
      this.selectedSource = null;
      this.selectedProfessor = null;
      this.selectedClass = null;
      this.selectedMember = null;
    },
    filterResults() {
      if (!this.sortByCheckins) {
        if (this.filterResultsByName) {
          const filter = this.filterResultsByName.toLowerCase();
          let results = [];
          for (const i of this.checkins) {
            let name = i.user ? i.user.fName + ' ' + i.user.lName : null;
            if (name) {
              name = name.toLowerCase();
              if (name.indexOf(filter) >= 0)
                results.push(i);
            }
          }
          this.filteredCheckins = results;
        } else {
          this.filteredCheckins = this.checkins;
        }
      } else if (this.checkinsAggregated) {
        if (this.filterResultsByName) {
          const filter = this.filterResultsByName.toLowerCase();
          let results = [];
          for (const i of this.checkinsAggregated) {
            let name = i.displayName;
            if (name) {
              name = name.toLowerCase();
              if (name.indexOf(filter) >= 0)
                results.push(i);
            }
          }
          this.filteredCheckinsAggregated = results;
        } else {
          this.filteredCheckinsAggregated = this.checkinsAggregated;
        }
      }
    },
    addCheckoutTime() {
      Api()
        .patch(
          "checkins/managedacademy/" +
          this.$store.state.managedAcademy +
          "/checkins/" + this.selectedStaffCheckin._id + "/checkout",
          {
            date: this.selectedStaffCheckin.checkoutTime,
          }
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.selectedStaffCheckin.checkout = this.selectedStaffCheckin.checkoutTime;
            this.selectedStaffCheckin = null;
            this.checkoutDialog = false;
            this.showAlert("success", this.$t("message.updated"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    showCheckoutDialog(i) {
      this.selectedStaffCheckin = i;
      const checkoutTime = new Date(this.selectedStaffCheckin.date);
      checkoutTime.setHours(checkoutTime.getHours() + 1)
      this.selectedStaffCheckin.checkoutTime = checkoutTime;
      this.checkoutDialog = true;
    },
    onIncludeProfessorsChange() {
      // "Only professors" only makes sense once professors are included at
      // all, and the switch is hidden as soon as this turns off — reset it
      // so a stale "yes" doesn't linger for next time it's shown.
      if (!this.includeProfessors) this.onlyProfessors = false;
      this.getAllAttendance();
    },
    onMinorsOnlyChange() {
      if (this.minorsOnly) this.adultsOnly = false;
      this.getAllAttendance();
    },
    onAdultsOnlyChange() {
      if (this.adultsOnly) this.minorsOnly = false;
      this.getAllAttendance();
    },
    getAllAttendance() {
      this.loadingAllAttendance = true;
      this.allAttendanceDialog = true;
      let url = "admins/academy/" +
        this.$store.state.managedAcademy +
        "/podium/" +
        this.interval + "?total_results=all";
      if (this.includeProfessors) url += "&include-professors=yes";
      if (this.includeProfessors && this.onlyProfessors) url += "&only-professors=yes";
      if (this.minorsOnly) url += "&minors-only=yes";
      if (this.adultsOnly) url += "&adults-only=yes";
      if (this.selectedLabel) url += "&label=" + encodeURIComponent(this.selectedLabel);
      Api2()
        .get(url)
        .then((response) => {
          const data = response.data;
          if (data) {
            this.allAttendance = data.topUsers;
          }
          this.loadingAllAttendance = false;
        });
    },
    onLabelChange(val) {
      this.selectedLabel = val && val.length ? val[val.length - 1] : null;
      this.getAllAttendance();
    },
    getCheckinsCSV() {
      this.loadingStaffCheckins = true;
      let postData = {
        byClass: this.byClassCheckins ? true : false
      };
      if (this.staffCheckinsProfessor) {
        const from = this.staffCheckinsFrom;
        if (from) {
          from.setHours(0);
          from.setMinutes(0);
          postData.from = from;
        }
        const to = this.staffCheckinsTo;
        if (to) {
          to.setHours(11);
          to.setMinutes(59);
          postData.to = to;
        }
        postData.professor = this.staffCheckinsProfessor;
        if (this.staffCheckinsNotPaid)
          postData.notPaid = true;
      }
      Api2()
        .post(
          "checkins/academy/" +
          this.$store.state.managedAcademy +
          "/staff/checkins?csv=yes", postData
        )
        .then((response) => {
          this.loadingStaffCheckins = false;
          let blob = new Blob([response.data], { type: "text/csv" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download =
            "Checkins_" +
            new Date().toISOString() +
            "_" +
            ".csv";
          link.click();
        })
        .catch((e) => {
          this.loadingStaffCheckins = false;
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    //Hours worked for a staff checkin. Whole hours by default, exact (to the
    //minute) when proration is on, so 2:00-3:20 pays 1.33h instead of 1h.
    staffCheckinHours(checkin) {
      if (!this.prorateStaffMinutes)
        return this.hoursBetween(checkin.date, checkin.checkout);
      return Math.round((this.staffCheckinMinutes(checkin) / 60) * 100) / 100;
    },
    //Remembered per academy in this browser, so an academy that pays by the
    //minute doesn't have to flip it every time and quietly pay whole hours
    //when somebody forgets.
    prorateStorageKey() {
      return "prorateStaffMinutes_" + this.$store.state.managedAcademy;
    },
    savedProrateStaffMinutes() {
      try {
        return localStorage.getItem(this.prorateStorageKey()) === "true";
      } catch (e) {
        return false;
      }
    },
    changeProrateStaffMinutes() {
      try {
        localStorage.setItem(this.prorateStorageKey(), this.prorateStaffMinutes ? "true" : "false");
      } catch (e) {
        //Storage blocked, the switch still works for this visit
      }
      this.updateExpense();
    },
    staffCheckinMinutes(checkin) {
      return Math.floor(
        Math.abs(new Date(checkin.checkout).getTime() - new Date(checkin.date).getTime()) / (1000 * 60)
      );
    },
    staffCheckinPay(checkin) {
      const rate = this.payRate ? new Number(this.payRate).valueOf() : 0;
      if (!this.prorateStaffMinutes) {
        const hours = this.hoursBetween(checkin.date, checkin.checkout);
        return hours > 0 ? rate * hours : 0;
      }
      //Use the raw minutes, not the 2-decimal hours shown in the list, so 80min at $10 is $13.33
      return Math.round(((rate * this.staffCheckinMinutes(checkin)) / 60) * 100) / 100;
    },
    updateExpense() {
      try {
        let total = 0;
        if (this.byClassCheckins) {
          //We just multiply rate by classs count
          for (const i of this.selectedStaffCheckins) {
            for (const j of this.staffCheckins) {
              if (i == j._id && !j.paid) {
                total += this.payRate ? new Number(this.payRate).valueOf() : 0;
                break;
              }
            }
          }
        } else {
          for (const i of this.selectedStaffCheckins) {
            for (let j of this.staffCheckins) {
              if (i == j._id && j.checkout && !j.paid) {
                j.paidAmount = this.staffCheckinPay(j);
                total += j.paidAmount;
                break;
              }
            }
          }
        }
        this.totalExpense = Math.round(total * 100) / 100;
      } catch (e) {
        console.log('Invalid number')
        this.totalExpense = 0;
      }
    },
    addExpense() {
      if (this.selectedStaffCheckins.length > 0) {
        this.addingExpense = true;
        let professor = null;
        for (const p of this.professors) {
          if (p._id.toString() == this.staffCheckinsProfessor) {
            professor = p.name;
            break;
          }
        }
        let checkinRates = {};
        for (const i of this.selectedStaffCheckins) {
          for (let j of this.staffCheckins) {
            if (i == j._id && j.checkout && !j.paid) {
              j.paidAmount = this.staffCheckinPay(j);
              checkinRates[j._id.toString()] = j.paidAmount;
              break;
            }
          }
        }
        const postData = {
          buyer: this.staffCheckinsProfessor,
          checkinRates: checkinRates,
          checkins: this.selectedStaffCheckins,
          items: [
            {
              name: professor + " " + (this.byClassCheckins ? this.$t('message.professor_classes') : this.$t('message.staff_hours')),
              amountDecimal: this.totalExpense,
              currency: this.currency,
              quantity: this.selectedStaffCheckins.length,
              description: this.currency + this.payRate + " " + (this.byClassCheckins ? this.$t('message.rate_per_class') : this.$t('message.hourly_salary'))
                + (!this.byClassCheckins && this.prorateStaffMinutes ? " (" + this.$t('message.prorated_by_minute') + ")" : "")
            },
          ],
          payRate: this.payRate,
          quantity: 1,
          completed: new Date(),
          processedBy: "desk",
          tags: ['wages-salaries']
        };
        Api2()
          .post("payments/academy/" + this.$store.state.managedAcademy + "/expenses/new", postData)
          .then(() => {
            this.showAlert("success", this.$t("message.success"));
            this.loadStaffCheckins();
            this.selectedStaffCheckins = [];
            this.addingExpense = false;
          })
          .catch((e) => {
            this.addingExpense = false;
            if (e)
              this.showAlert("error", "Ops!", this.$t("message.error_generic"));
          });
      }
    },
    switchStaffCheckinsMode(byClass) {
      if (this.byClassCheckins === byClass) return;
      this.byClassCheckins = byClass;
      //Everything below belongs to the other mode: the selection kept the
      //expense card up with its old total, and an hourly rate is not a rate
      //per class. Reloading brings back the right rate for this mode.
      this.staffCheckins = [];
      this.selectAllStaffCheckins = false;
      this.selectedStaffCheckins = [];
      this.totalExpense = 0;
      this.payRate = null;
      if (this.staffCheckinsProfessor) this.loadStaffCheckins();
    },
    loadStaffCheckins() {
      this.selectAllStaffCheckins = false;
      this.loadingStaffCheckins = true;
      let postData = {
        byClass: this.byClassCheckins ? true : false
      };
      if (this.staffCheckinsProfessor) {
        const from = this.staffCheckinsFrom;
        if (from) {
          from.setHours(0);
          from.setMinutes(0);
          postData.from = from;
        }
        const to = this.staffCheckinsTo;
        if (to) {
          to.setHours(11);
          to.setMinutes(59);
          postData.to = to;
        }
        postData.professor = this.staffCheckinsProfessor;
        if (this.staffCheckinsNotPaid)
          postData.notPaid = true;
      }
      Api2()
        .post(
          "checkins/academy/" +
          this.$store.state.managedAcademy +
          "/staff/checkins", postData
        )
        .then((response) => {
          this.loadingStaffCheckins = false;
          let tmp = response.data.checkins;
          const totalCheckins = tmp.length;
          let lateCheckins = 0;
          if (this.byClassCheckins) {
            for (let c of tmp) {
              c.isLate = this.isLateCheckin(c.date, c.startTime);
              if (c.isLate)
                lateCheckins++;
            }
          }
          this.staffCheckins = tmp;
          if (tmp && tmp.length) {
            const onTimeCheckins = totalCheckins - lateCheckins;
            this.onTimeRate = (onTimeCheckins / totalCheckins) * 100;
          }
          this.currency = response.data.currencySymbol;
          this.payRate = this.byClassCheckins ? response.data.ratePerClass : response.data.ratePerHour;
        });
    },
    goToPodiumUser(u) {
      if (u)
        this.goToMember(u.id, u.name);
    },
    getComparisonNumbers(data) {
      let previousValue = data[0].value;
      let currentValue = data[1].value;
      this.comparisonNumbers = [];
      this.comparisonNumbers.push({
        id: 1,
        title: this.$t("message.previous_interval"),
        percent: 0,
        value: previousValue,
      });
      let percent = 0;
      if (previousValue == 0) {
        percent = Math.round((currentValue - previousValue) * 100);
      } else {
        percent = Math.round(
          ((currentValue - previousValue) / previousValue) * 100
        );
      }
      this.comparisonNumbers.push({
        id: 2,
        title: this.$t("message.this_interval"),
        percent: percent,
        value: data[1].value,
      });
    },
    getCheckinsCalendar() {
      if (this.$store.state.selectedAcademy) {
        Api2()
          .get(
            "admins/academy/" +
            this.$store.state.selectedAcademy +
            "/aggregates/calendar/monthly/36"
          )
          .then((response) => {
            let series = [];
            //Up to 3 years, we will get the 12 months
            const currentYear = new Date().getFullYear();
            const yearsMap = {};

            for (let year = currentYear - 3; year <= currentYear; year++) {
              yearsMap[year] = [];

              for (let month = 1; month <= 12; month++) {
                yearsMap[year].push({
                  month,
                  checkins: 0,
                  x: month,
                  y: 0
                });
              }
            }
            for (const d of response.data) {
              const year = d._id.year;
              const month = d._id.month;
              let months = yearsMap[year];
              for (const m of months) {
                if (month == m.month) {
                  m.checkins = d.checkins;
                  m.x = this.$t("message.month") + ' ' + month;
                  m.y = d.checkins;
                }
              }
            }
            Object.keys(yearsMap).forEach(key => {
              const months = yearsMap[key];
              series.push({
                name: key,
                data: months
              });
            });

            this.calendarChartData = series;
            this.calendarChartOptions = {
              theme: {
                mode: this.isDarkMode() ? 'dark' : 'light',
              },
              chart: {
                background: 'none',
                toolbar: {
                  show: false
                },
              },
              colors: [this.themeColor('graph1')],
            };
          })
          .catch((e) => {
            console.log(e)
            this.loading = false;
            if (e)
              this.showAlert(
                "error",
                this.$t("message.error_generic")
              );
          });
      }
    },
    getHeatMap() {
      const from = new Date();
      from.setHours(0);
      from.setMinutes(0);
      const to = new Date();
      to.setHours(11);
      to.setMinutes(59);
      to.setHours(to.getHours() + 3);
      if (this.interval == 0)
        return;
      else if (this.interval == 1) {
        from.setDate(from.getDate() - 7);
      } else if (this.interval == 2) {
        from.setDate(from.getDate() - 30);
      } else {
        return;
      }

      this.heatMap.loading = true;
      const params = { from: from, to: to };
      Api2()
        .post(
          "admins/academy/" +
          this.$store.state.selectedAcademy +
          "/attendance/byhour", params
        )
        .then((response) => {
          const data = response.data;
          if (data && data.data && data.data.data.length > 0) {
            let graphData = data.data.data;
            let graphDays = data.data.days;
            let series = [];
            for (const d of graphData) {
              let hourData = [];
              let index = 0;
              for (const dd of d.days) {
                const dayString = this.$d(
                  new Date(graphDays[index].date),
                  "month_day",
                  this.$i18n.locale
                );
                hourData.push({ x: dayString, y: dd });
                index++;
              }
              series.push({ name: d.hour, data: hourData });
            }
            const vm = this;
            this.heatMap.options = {
              chart: {
                height: 350,
                type: 'heatmap',
                toolbar: {
                  show: false
                },
              },
              dataLabels: {
                enabled: false
              },
              colors: [this.themeColor('graph1')],
              theme: {
                mode: this.isDarkMode() ? 'dark' : 'light',
              },
              yaxis: {
                title: {
                  text: 'Hour of the day'
                },
              },
              tooltip: {
                y: {
                  formatter: function (value) {
                    return value + ' ' + vm.$t("message.checkins");
                  }
                },
                x: {
                  formatter: function (value) {
                    return value + ' ' + vm.$t("message.checkins");
                  }
                }
              },
            };
            this.heatMap.data = series;
          }
          this.heatMap.loading = false;
        });
    },
    getAggregates(selectedInterval) {
      if (this.$store.state.selectedAcademy) {
        this.loading = true;
        const intervals = this.getIntervalOptions();
        for (const i of intervals) {
          if (selectedInterval == i.value) {
            this.intervalText = i.text;
            break;
          }
        }
        this.getHeatMap();
        Api2()
          .get(
            "admins/academy/" +
            this.$store.state.selectedAcademy +
            "/aggregates/" +
            this.interval
          )
          .then((response) => {
            this.loading = false;
            const data = response.data;
            if (data) {
              try {
                this.from = this.$d(
                  new Date(data.from),
                  "month_day_year",
                  this.$i18n.locale
                );
                this.to = this.$d(
                  new Date(data.to),
                  "month_day_year",
                  this.$i18n.locale
                );

                this.buildGraph1(data.totalCheckinsVsInterval);
                this.buildGraph2(data.checkinsByDay);
                this.buildGraph3(data.checkins);
                this.buildGraph4(data.totalCheckinsByDay);
                this.buildNewMembers(data.newMembers);
                this.getComparisonNumbers(data.comparison);
              } catch (e) {
                console.log(e);
              }
            }
          })
          .catch((e) => {
            this.loading = false;
            if (e)
              this.showAlert(
                "error",
                "Oops!",
                this.$t("message.error_generic")
              );
          });
        Api2()
          .get(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/podium/" +
            this.interval
          )
          .then((response) => {
            this.loading = false;
            const data = response.data;
            if (data) {
              this.topUsers = this.getPodiumData(data.topUsers);
            }
          });
      }
    },
    getPodiumData(data) {
      if (data && data.length == 3) {
        let podiumData = [];
        podiumData.push({
          name: data[1].displayName,
          id: data[1]._id,
          place: 2,
          avatar: data[1].userImageSmall,
          points: data[1].checkins,
          belt: data[1].belt
        });
        podiumData.push({
          name: data[0].displayName,
          id: data[0]._id,
          place: 1,
          avatar: data[0].userImageSmall,
          points: data[0].checkins,
          belt: data[0].belt
        });
        podiumData.push({
          name: data[2].displayName,
          id: data[2]._id,
          place: 3,
          avatar: data[2].userImageSmall,
          points: data[2].checkins,
          belt: data[2].belt
        });
        return podiumData;
      }
    },
    buildGraph1(data) {
      let withoutCheckin = data.totalStudents - data.intervalUniqueCheckins;
      if (withoutCheckin < 0)
        withoutCheckin = 0;
      this.chart1Options = {
        legend: {
          position: 'bottom'
        },
        stroke: { show: false },
        theme: {
          mode: this.isDarkMode() ? 'dark' : 'light',
        },
        chart: { background: 'none' },
        colors: [this.themeColor('graph1'), this.themeColor('graph2')],
        labels: [this.$t("message.without_checkin"), this.$t("message.with_checkin")]
      };
      this.chartData1 = [withoutCheckin, data.intervalUniqueCheckins];
    },
    buildGraph2(data) {
      let graphData = [];
      let xCategories = [];
      for (const d of data) {
        var day = new Date();
        day.setUTCFullYear(d._id.year);
        day.setUTCMonth(d._id.month - 1);
        day.setUTCDate(d._id.dayOfMonth);
        graphData.push(d.checkins);
        xCategories.push(this.$d(day, "month_day", this.$i18n.locale))
      }
      this.chart2Options = {
        colors: [this.themeColor('graph1')],
        labels: [this.$t("message.without_checkin"), this.$t("message.with_checkin")],
        xaxis: {
          categories: xCategories,
          labels: {
            show: true,
            style: {
              colors: this.themeColor('title')
            },
          }
        },
        yaxis: {
          labels: {
            show: true,
            style: {
              colors: this.themeColor('title')
            },
            formatter: function (val) {
              return val.toFixed(0)
            }
          }
        },
        dataLabels: {
          enabled: true,
        },
        stroke: {
          curve: 'smooth'
        },
        theme: {
          mode: this.isDarkMode() ? 'dark' : 'light',
        },
        chart: {
          toolbar: {
            show: false
          },
          background: 'none'
        },
        legend: {
          position: 'bottom'
        },
        grid: {
          borderColor: this.themeColor('gray'),
        },
      };
      this.chartData2 = [{
        name: this.$t("message.attendance"),
        data: graphData
      }];
    },
    buildGraph3(data) {
      //colors: [this.themeColor('secondary'), this.themeColor('primary'), this.themeColor('graph'),this.themeColor('gray')],
      this.chart3Options = {
        theme: {
          mode: this.isDarkMode() ? 'dark' : 'light',
        },
        chart: {
          background: 'none'
        },
        stroke: {
          show: false
        },
        legend: {
          position: 'bottom'
        },
        colors: [this.themeColor('graph1'), this.themeColor('graph2'), this.themeColor('graph3'), this.themeColor('secondary')],
        labels: [this.$t("message.app"), this.$t("message.tablet"), this.$t("message.manual"), this.$t("message.visitors")]
      };
      this.chartData3 = [data.appCheckins, data.tabletCheckins, data.manualCheckins, data.visitorCheckins];
    },
    buildGraph4(data) {
      let graphData = [
        {
          name: this.$t("message.morning"),
          data: [],
        },
        {
          name: this.$t("message.afternoon"),
          data: [],
        },
        {
          name: this.$t("message.evening"),
          data: [],
        }
      ];
      let days = [];
      for (const weekday of this.weekdays) {
        const weekDayData = data[weekday];
        let morning = 0;
        let afternoon = 0;
        let evening = 0;
        for (const d of weekDayData) {
          const checkinDate = new Date(d.date);
          const checkinHour = checkinDate.getHours();
          if (checkinHour <= 11) {
            morning++;
          } else if (checkinHour > 11 && checkinHour <= 15) {
            afternoon++;
          } else {
            evening++;
          }
        }
        days.push(this.$t("message." + weekday));
        graphData[0].data.push(morning);
        graphData[1].data.push(afternoon);
        graphData[2].data.push(evening);
      }
      this.chart4Options = {
        theme: {
          mode: this.isDarkMode() ? 'dark' : 'light',
        },
        chart: {
          toolbar: {
            show: false
          },
          background: 'none'
        },
        xaxis: {
          categories: days
        },
        legend: {
          position: 'bottom'
        },
        colors: [this.themeColor('graph1'), this.themeColor('graph2'), this.themeColor('graph3')],
      };
      this.chartData4 = graphData;
    },
    buildNewMembers(data) {
      if (data && data.length > 0) {
        this.newMembers = data;
      }
    },
    clickTab(tab) {
      if (tab != this.activeTab) {
        this.navigateTo({
          name: "viewattendancetabs",
          params: { tabId: tab },
        });
      }
    },
    goToTransaction(id) {
      if (id)
        this.navigateTo({ name: 'viewtransaction', params: { sessionId: id } });
    },
    sortByCheckinCounts() {
      this.filterResultsByName = null;
      if (this.sortByCheckins && this.selectedMonth) {
        let d = new Date(this.selectedMonth);
        d.setDate(1);
        d.setHours(0);
        d.setMinutes(0);
        let postData = { month: d };
        if (this.selectedClassName)
          postData.className = this.selectedClassName;
        Api2()
          .post(
            "admins/academy/" + this.$store.state.managedAcademy + "/sort/checkins?total_results=all",
            postData
          )
          .then((response) => {
            this.checkinsAggregated = response.data.topUsers;
            this.filteredCheckinsAggregated = response.data.topUsers;
          });
      } else {
        this.checkinsAggregated = null;
      }
    },
    showDeleteCheckinDialog(id) {
      this.checkinToDelete = id;
      this.deleteCheckinDialog = true;
    },
    removeManualCheckin() {
      const id = this.checkinToDelete;
      if (this.checkins) {
        Api()
          .delete(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/checkins/" + id
          )
          .then((response) => {
            if (response) {
              let index = 0;
              for (const checkin of this.checkins) {
                if (checkin._id == id) {
                  this.checkins.splice(index, 1);
                  break;
                }
                index++;
              }
              this.deleteCheckinDialog = false;
              this.checkinToDelete = null;
            }
          }).catch((e) => {
            if (e)
              this.showAlert("error", "Ops!", this.$t("message.error_generic"));
          });
      }
    },
    manualMemberCheckin() {
      const value = this.classCheckin.value;
      const professorId = this.classCheckin.professor;
      const checkinData = this.getManualCheckinClassData(this.schedule, value, this.classesMap);
      const assistantProfessors = checkinData.assistantProfessors;
      if (checkinData) {
        let className = checkinData.class.name;
        let classDay = checkinData.class.day;
        let scheduleClassId = !checkinData.customClass ? checkinData.class._id : null;
        let academyClassId = checkinData.customClass ? checkinData.class._id : null;
        const checkinTime = checkinData.checkinTime;
        const data = {
          date: this.dateCheckin,
          dayOfMonth: checkinTime.getDate(),
          member: this.memberCheckin,
          professor: professorId,
          assistantProfessors: assistantProfessors,
          className: className,
          classDay: classDay,
          startTime: checkinData.class.start,
          yearAtCity: checkinTime.getFullYear(),
          monthAtCity: checkinTime.getMonth() + 1,
          checkinTime: checkinTime,
          academyClassId: academyClassId,
          scheduleClassId: scheduleClassId,
        };
        this.checkingIn = true;
        Api2()
          .post(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/checkin/member/" + (this.memberCheckin ? this.memberCheckin : this.visitorCheckin),
            data
          )
          .then((response) => {
            if (response) this.showAlert("success", this.$t("message.success"));
            this.memberCheckin = null;
            this.classCheckin = null;
          }).catch((e) => {
            if (e)
              this.showAlert("error", "Ops!", this.$t("message.error_generic"));
          }).finally(() => {
            this.checkingIn = false;
          });
      }
    },
    clearAttendances() {
      this.rollingNumbers = null;
      this.checkins = null;
      this.totalResults = 0;
      this.filterResultsByName = null;
    },
    getMemberAttendanceReport() {
      const start = new Date(this.aggStartDate || new Date());
      start.setHours(0, 0, 0, 0);
      const end = new Date(this.aggEndDate || new Date());
      end.setHours(23, 59, 59, 999);
      let postData = {
        start: start,
        end: end,
      };
      if (this.selectedMember) postData["memberId"] = this.selectedMember;
      if (this.selectedProfessor) postData["professorId"] = this.selectedProfessor;
      if (this.selectedClassName) {
        postData["name"] = this.selectedClassName;
      } else if (this.selectedClass) {
        postData["classId"] = this.selectedClass;
      }
      if (this.reportGroupBy !== "none") {
        postData["groupBy"] = this.reportGroupBy;
        postData["aggregateByUser"] = this.reportAggregateByUser;
      }
      Api2()
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/attendance/user/report",
          postData
        )
        .then((response) => {
          this.showAlert("success", this.$t("message.report_in_progress", [response.data.sentTo]));
        });
    },
    getAggregateResults() {
      this.loadingAggResults = true;
      this.aggResults = null;
      const start = new Date(this.aggStartDate || new Date());
      start.setHours(0, 0, 0, 0);
      const end = new Date(this.aggEndDate || new Date());
      end.setHours(23, 59, 59, 999);
      Api2()
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/attendance/user/report",
          {
            start,
            end,
            groupBy: this.reportGroupBy,
            aggregateByUser: false,
            resultType: 'json',
          }
        )
        .then((response) => {
          this.aggResults = response.data.rows;
        })
        .catch(() => {
          this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        })
        .finally(() => {
          this.loadingAggResults = false;
        });
    },
    getRollingNumbers() {
      var postData = {};
      if (this.selectedMonth) {
        let d = new Date(this.selectedMonth);
        d.setDate(1);
        d.setHours(0);
        d.setMinutes(0);
        postData["month"] = d;
      }
      if (this.selectedMember) postData["memberId"] = this.selectedMember;
      if (this.selectedProfessor) postData["professorId"] = this.selectedProfessor;
      if (this.selectedClassName) {
        postData["name"] = this.selectedClassName;
      } else if (this.selectedClass) {
        postData["classId"] = this.selectedClass;
      }
      Api2()
        .post(
          "admins/academy/" +
          this.$store.state.selectedAcademy +
          "/attendance/aggregates",
          postData
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            var previousValue = null;
            this.rollingNumbers = [];
            for (const d of data) {
              if (previousValue != null) {
                var currentValue = d.value;
                var percent = 0;
                if (previousValue == 0) {
                  percent = Math.round((currentValue - previousValue) * 100);
                } else {
                  percent = Math.round(
                    ((currentValue - previousValue) / previousValue) * 100
                  );
                }
                this.rollingNumbers.push({
                  title: this.$d(
                    new Date(d.month),
                    "month_year",
                    this.$i18n.locale
                  ),
                  percent: percent,
                  value: d.value,
                });
              }
              previousValue = d.value;
            }
          }
        });
    },
    getTimezoneDay(date) {
      return new Date(date).toLocaleString("en-US", {
        timeZone: this.timeZone,
      });
    },
    goToAcademy(id) {
      if (!this.isSmallerDisplay()) {
        console.log(id);
        //window.open('https://google.com');
      }
    },
    getAttendance(append) {
      this.sortByCheckins = false;
      this.filterResultsByName = null;
      this.loading = true;
      let d;
      if (!append) {
        this.index = 0;
        this.checkins = [];
        this.exclude = [];
      }
      let postData = {
        exclude: this.exclude,
        members: this.membersOnly,
        visitors: this.visitorsOnly,
      };
      if (this.selectedDate) {
        d = this.selectedDate;
        d.setHours(0);
        d.setMinutes(0);
        postData["date"] = d;
      }
      if (this.selectedMonth) {
        d = new Date(this.selectedMonth);
        d.setDate(1);
        d.setHours(0);
        d.setMinutes(0);
        postData["month"] = d;
      }
      if (this.selectedMember) postData["memberId"] = this.selectedMember;
      if (this.selectedProfessor) postData["professorId"] = this.selectedProfessor;
      if (this.selectedClassName) {
        postData["name"] = this.selectedClassName;
      } else if (this.selectedClass) {
        postData["classId"] = this.selectedClass;
      }
      if (this.selectedSource)
        postData["source"] = this.selectedSource;
      if (this.selectedLocation) postData["location"] = this.selectedLocation;
      if (this.selectedDiscipline) postData["discipline"] = this.selectedDiscipline;
      Api2()
        .post(
          "admins/academy/" + this.$store.state.selectedAcademy + "/attendance?index=" + this.index,
          postData
        )
        .then((response) => {
          const data = response.data;
          if (!this.checkins)
            this.checkins = [];
          //numberToHoursAndMinutes(checkin.start)
          let tmp = data.checkins;
          for (let c of tmp) {
            const time = this.numberToHoursAndMinutes(c.start);
            c.isLate = this.isLateCheckin(c.date, c.start)
            if (time && time.hours !== undefined)
              c.time = time.hours + ":" + String(time.minutes).padStart(2, "0");
          }

          this.checkins = this.checkins.concat(tmp);
          this.filteredCheckins = this.checkins;
          this.loading = false;
          this.index = this.index + data.count;
          this.viewMore = data.hasMore;
          this.totalResults = data.total;
        });
    },
    getAttendanceCSV() {
      this.loadingAttendanceCsv = true;
      let d;
      let postData = {
        members: this.membersOnly,
        visitors: this.visitorsOnly,
      };
      if (this.selectedDate) {
        d = this.selectedDate;
        d.setHours(0);
        d.setMinutes(0);
        postData["date"] = d;
      }
      if (this.selectedMonth) {
        d = new Date(this.selectedMonth);
        d.setDate(1);
        d.setHours(0);
        d.setMinutes(0);
        postData["month"] = d;
      }
      if (this.selectedMember) postData["memberId"] = this.selectedMember;
      if (this.selectedProfessor) postData["professorId"] = this.selectedProfessor;
      if (this.selectedClassName) {
        postData["name"] = this.selectedClassName;
      } else if (this.selectedClass) {
        postData["classId"] = this.selectedClass;
      }
      if (this.selectedSource)
        postData["source"] = this.selectedSource;
      Api2()
        .post(
          "admins/academy/" + this.$store.state.selectedAcademy + "/attendance?csv=yes",
          postData
        )
        .then((response) => {
          this.loadingAttendanceCsv = false;
          let blob = new Blob([response.data], { type: "text/csv" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "attendance_" + new Date().toISOString() + ".csv";
          link.click();
        })
        .catch((e) => {
          this.loadingAttendanceCsv = false;
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    viewDetails(obj) {
      if (obj) {
        this.navigateTo({ name: "viewmember", params: { memberid: obj._id } });
      }
    },
    wasOntime(checkin) {
      let d = new Date(checkin.date);
      const h = d.getHours();
      const m = d.getMinutes();
      const checkinTime = parseFloat(h + "." + String(m).padStart(2, "0"));
      if (checkinTime < checkin.startTime) return true;
      return false;
    },
  },
};
</script>

<style scoped>
.graph-area {
  height: 445px;
}

.rolling-value {
  font-size: 2.5em;
}

.rolling-percent {
  font-size: 1.5em;
  margin-left: -10px;
}
</style>
